import React, { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import { Select, Input, Spin, Empty, Modal, Table, Button, Form, DatePicker, InputNumber, Popconfirm, message, Progress, Breadcrumb, Tag, Space } from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { customThemes } from '../../../../themeConfig';
import { saveAs } from 'file-saver';
import { calculateCFT, getWidthQtyTotal } from '../../../../utils/cftCalculator';
import * as XLSX from 'xlsx';

interface User {
  user_id: number;
  user_name: string;
  user_email: string;
}

interface FlatWorkOrder {
  pid_id: number;
  wo_id: number;
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  pi_created_by: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  product_id: number;
  product_name: string;
  qty: number;
  employee_fname?: string;
  employee_lname?: string;
  wo_status?: string;
  daysLeft: number;
  statusText: string;
  borderColor: string;
  glowColor: string;
  badgeBg: string;
  total_complete?: number;
  wo_due_date?: string;
  wo_number?: string;
  wo_employee_id?: number;
  isMachinery?: boolean;
  wo_done_on_time?: 'Y' | 'N' | null;
  isCompleted?: boolean;
  progressPercent?: number;
  completedComponentsCount?: number;
  totalComponentsCount?: number;
}

const FALLBACK_DEPARTMENTS = [
  { key: 'machinery', label: 'Machinery', color: '#06b6d4', id: 10 },
  { key: 'assembly', label: 'Assembly', color: '#10b981', id: 11 },
  { key: 'sanding', label: 'Sanding', color: '#ec4899', id: 12 },
  { key: 'finishing', label: 'Finishing', color: '#ff7875', id: 13 },
  { key: 'packed', label: 'Packed', color: '#52c41a', id: 7 },
];

const getIssuedColor = (issuedQty: number, reqQty: number): string => {
  if (issuedQty <= 0) return "#8c8c8c";
  if (reqQty <= 0) return "#ff4d4f";
  const percent = parseFloat(((issuedQty / reqQty) * 100).toFixed(4));
  if (percent < 100) return "#faad14"; // Yellow
  if (percent <= 103) return "#52c41a"; // Green
  return "#ff4d4f"; // Red
};

const DailyProgressProductDetail: React.FC = () => {
  const { pi_id, pid_id } = useParams<{ pi_id: string; pid_id: string }>();
  const navigate = useNavigate();

  const [productDetail, setProductDetail] = useState<any>(null);
  const [departmentsList, setDepartmentsList] = useState<any[]>(FALLBACK_DEPARTMENTS);
  const DEPARTMENTS = departmentsList;

  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [boms, setBoms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [woDetailsMap, setWoDetailsMap] = useState<Record<number, any[]>>({});

  // Timber and Issue Tracking States
  const [piDetail, setPiDetail] = useState<any>(null);
  const [woods, setWoods] = useState<any[]>([]);
  const [woodTypes, setWoodTypes] = useState<any[]>([]);
  const [issueMasterList, setIssueMasterList] = useState<any[]>([]);
  const [trackIssueModalVisible, setTrackIssueModalVisible] = useState<boolean>(false);

  // Filters
  const [selectedDept, setSelectedDept] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleClearFilters = () => {
    setSelectedDept(null);
    setSelectedUser(null);
    setSearchQuery('');
  };

  // Daily Completion Details Modal State
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedWO, setSelectedWO] = useState<FlatWorkOrder | null>(null);
  const [dailyDetails, setDailyDetails] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Form Modal State (Add/Edit)
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingDetail, setEditingDetail] = useState<any | null>(null);
  const [form] = Form.useForm();

  // Download PDF Modal State
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedWOIds, setSelectedWOIds] = useState<number[]>([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const isMachineryWO = useMemo(() => {
    if (!selectedWO || !employees.length) return false;
    const woEmployee = employees.find(e => e.employee_id === selectedWO.wo_employee_id);
    if (!woEmployee || !woEmployee.employee_department) return false;

    const machineryDept = departmentsList.find(d => String(d.label).toLowerCase().includes("machinery"));
    if (!machineryDept) return false;

    const deptIds = String(woEmployee.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
    return deptIds.includes(Number(machineryDept.id));
  }, [selectedWO, employees, departmentsList]);

  const matchedBom = useMemo(() => {
    if (!selectedWO || !boms.length) return null;
    return boms.find(b => Number(b.bom_product_id) === Number(selectedWO.product_id));
  }, [selectedWO, boms]);

  const bomComponents = useMemo(() => {
    if (!matchedBom || !Array.isArray(matchedBom.components)) return [];
    return matchedBom.components;
  }, [matchedBom]);

  const showComponentDropdown = isMachineryWO && bomComponents.length > 0;

  const fetchDailyDetails = async (woId: number) => {
    try {
      setLoadingDetails(true);
      const res = await apiRequest<any[]>({
        method: 'get',
        url: `/work-order-details/${woId}`
      });
      const list = res || [];
      setDailyDetails(list);
      setWoDetailsMap(prev => ({
        ...prev,
        [woId]: list
      }));
    } catch (err) {
      console.error(err);
      message.error('Failed to load daily details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const openDetailsModal = (wo: FlatWorkOrder) => {
    setSelectedWO(wo);
    setDetailsModalVisible(true);
    fetchDailyDetails(wo.wo_id);
  };

  const handleOpenAdd = () => {
    setEditingDetail(null);
    form.resetFields();
    form.setFieldsValue({
      wod_created_on: dayjs(),
      wod_qty: undefined,
      wod_remarks: '',
      wod_component: undefined
    });
    setFormModalVisible(true);
  };

  const handleOpenEdit = (record: any) => {
    setEditingDetail(record);
    form.resetFields();

    const match = String(record.wod_remarks || '').match(/^\[(.*?)\](?: (.*))?$/);
    const componentName = match ? match[1] : undefined;
    const remarks = match ? (match[2] || '') : record.wod_remarks;

    form.setFieldsValue({
      wod_created_on: dayjs(record.wod_created_on),
      wod_qty: record.wod_qty,
      wod_remarks: remarks,
      wod_component: componentName
    });
    setFormModalVisible(true);
  };

  const handleSaveDetail = async (values: any) => {
    if (!selectedWO) return;
    try {
      let finalRemarks = values.wod_remarks || '';
      if (values.wod_component) {
        finalRemarks = `[${values.wod_component}]` + (values.wod_remarks ? ` ${values.wod_remarks}` : '');
      }

      const payload = {
        wod_id: editingDetail?.wod_id || null,
        wod_wo_id: selectedWO.wo_id,
        wod_qty: Number(values.wod_qty),
        wod_remarks: finalRemarks,
        wod_created_on: editingDetail
          ? (values.wod_created_on ? values.wod_created_on.format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'))
          : dayjs().format('YYYY-MM-DD HH:mm:ss')
      };


      await apiRequest({
        method: 'post',
        url: '/work-order-details',
        data: payload
      });

      message.success(editingDetail ? 'Daily detail updated successfully' : 'Daily detail added successfully');
      setFormModalVisible(false);
      setEditingDetail(null);
      form.resetFields();

      fetchDailyDetails(selectedWO.wo_id);
      fetchData(); // Refresh main lists
    } catch (err) {
      console.error(err);
      message.error('Failed to save daily detail');
    }
  };

  const handleDeleteDetail = async (wodId: number) => {
    if (!selectedWO) return;
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-work-order-details/${wodId}`
      });
      message.success('Daily detail deleted successfully');
      fetchDailyDetails(selectedWO.wo_id);
      fetchData();
    } catch (err) {
      console.error(err);
      message.error('Failed to delete daily detail');
    }
  };

  const handleDownloadSelectedPDF = async () => {
    if (selectedWOIds.length === 0) {
      message.warning('Please select at least one work order');
      return;
    }

    try {
      setDownloadingPdf(true);
      const woIdsStr = selectedWOIds.join(',');
      const blob = await apiRequest<Blob>({
        method: 'get',
        url: `/download-work-orders?wo_ids=${woIdsStr}`,
        config: { responseType: 'blob' }
      });
      saveAs(blob, `Work-Orders-${dayjs().format('YYYY-MM-DD-HHmmss')}.pdf`);
      message.success('PDF downloaded successfully');
      setDownloadModalVisible(false);
      setSelectedWOIds([]);
    } catch (err) {
      console.error(err);
      message.error('Failed to download work orders PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Context styles
  const context = useOutletContext<any>() || {};
  const darkMode = context.darkMode || false;
  const baseTheme = darkMode ? customThemes.dark : customThemes.light;
  const themeStyles = { ...baseTheme, ...context.themeStyles };

  const customLayoutBg = darkMode ? '#0f172a' : '#f8fafc';
  const customCardBg = darkMode ? '#1e293b' : '#ffffff';
  const customCardBorder = darkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(226, 232, 240, 0.8)';
  const customTextMain = darkMode ? '#f8fafc' : '#0f172a';
  const customTextSub = darkMode ? '#94a3b8' : '#64748b';

  // Fetch Product details, Work Orders, Users, Departments, BOMs, Woods, Issues, etc.
  const fetchData = async () => {
    try {
      setLoading(true);
      const promises: Promise<any>[] = [
        apiRequest<any[]>({ method: 'get', url: '/get-work-orders' }),
        apiRequest<any>({ method: 'get', url: '/users' }),
        apiRequest<any[]>({ method: 'get', url: '/departments' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/employees?status=1' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/get-bom' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/woods' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/wood-types' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/issue-master?type=Timber' }).catch(() => [])
      ];

      if (pid_id) {
        promises.push(
          apiRequest<any>({
            method: 'get',
            url: `/get-track-pi-product-detail/${pid_id}`,
          }).catch(() => null)
        );
      } else {
        promises.push(Promise.resolve(null));
      }

      if (pi_id) {
        promises.push(
          apiRequest<any>({
            method: 'get',
            url: `/get-track-pi-detail/${pi_id}`,
          }).catch(() => null)
        );
      } else {
        promises.push(Promise.resolve(null));
      }

      const [woData, userData, deptData, empData, bomData, woodsData, woodTypesData, issueData, prodDetail, piDetailData] = await Promise.all(promises);

      const colorsPalette = ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];
      const activeDepts = (deptData || []).filter((d: any) => d.department_status === 'Y' || Number(d.department_status) === 1 || String(d.department_status) === '1');

      const ALLOWED_DEPARTMENTS = ["Machinery", "Assembly", "Sanding", "Finishing", "Packing"];
      const mappedDepts = activeDepts
        .map((dept: any, index: number) => ({
          key: String(dept.department_name).toLowerCase().replace(/\s+/g, '_'),
          label: dept.department_name,
          color: colorsPalette[index % colorsPalette.length],
          id: dept.department_id
        }))
        .filter((d: any) =>
          ALLOWED_DEPARTMENTS.some(allowed => allowed.toLowerCase() === d.label.toLowerCase())
        )
        .sort((a: any, b: any) => {
          const indexA = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === a.label.toLowerCase());
          const indexB = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === b.label.toLowerCase());
          return indexA - indexB;
        });

      let currentDepts = departmentsList;
      if (mappedDepts.length > 0) {
        setDepartmentsList(mappedDepts);
        currentDepts = mappedDepts;
      } else {
        const filteredFallbacks = FALLBACK_DEPARTMENTS
          .filter((d: any) =>
            ALLOWED_DEPARTMENTS.some(allowed => allowed.toLowerCase() === d.label.toLowerCase())
          )
          .sort((a: any, b: any) => {
            const indexA = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === a.label.toLowerCase());
            const indexB = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === b.label.toLowerCase());
            return indexA - indexB;
          });
        setDepartmentsList(filteredFallbacks);
        currentDepts = filteredFallbacks;
      }

      const machineryDept = currentDepts.find((d: any) => String(d.label).toLowerCase().includes("machinery"));

      const machineryWOs = (woData || []).filter((wo: any) => {
        const emp = (empData || []).find((e: any) => e.employee_id === wo.wo_employee_id);
        const empInMachinery = !!(emp && emp.employee_department && machineryDept &&
          String(emp.employee_department).split(',').map((id: any) => parseInt(String(id).trim(), 10)).includes(Number(machineryDept.id)));
        const woDeptInMachinery = !!(wo.wo_department_name && String(wo.wo_department_name).toLowerCase().includes('machinery'));
        return empInMachinery || woDeptInMachinery;
      });

      const detailsMap: Record<number, any[]> = {};
      if (machineryWOs.length > 0) {
        const detailsResults = await Promise.all(
          machineryWOs.map((wo: any) =>
            apiRequest<any[]>({ method: 'get', url: `/work-order-details/${wo.wo_id}` })
              .then(res => ({ wo_id: wo.wo_id, details: res || [] }))
              .catch(() => ({ wo_id: wo.wo_id, details: [] }))
          )
        );
        detailsResults.forEach(({ wo_id, details }) => {
          detailsMap[wo_id] = details;
        });
      }

      setWoDetailsMap(detailsMap);
      setWorkOrders(woData || []);
      setUsers(userData || []);
      setEmployees(empData || []);
      setBoms(bomData || []);
      setWoods(woodsData || []);
      setWoodTypes(woodTypesData || []);
      setIssueMasterList(issueData || []);
      if (prodDetail) {
        setProductDetail(prodDetail);
      }
      if (piDetailData) {
        setPiDetail(piDetailData);
      }
    } catch (error) {
      console.error('Fetch product track data error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pid_id, pi_id]);

  // Construct Flat Work Orders Array filtered for this Product / PI
  const flatWorkOrders = useMemo(() => {
    // Filter work orders that belong to this product or PI
    const filteredByProduct = workOrders.filter((wo) => {
      if (pid_id && String(wo.wo_pid_detail_id) === String(pid_id)) {
        return true;
      }
      if (pi_id && String(wo.wo_pi_id) === String(pi_id)) {
        if (productDetail?.pid_product_id && String(wo.product_id) === String(productDetail.pid_product_id)) {
          return true;
        }
        if (!pid_id) return true;
      }
      return false;
    });

    // If no exact match found, but we have work orders for this PI, fallback to PI work orders
    const targetWOs = filteredByProduct.length > 0
      ? filteredByProduct
      : workOrders.filter((wo) => pi_id && String(wo.wo_pi_id) === String(pi_id));

    return targetWOs.map((wo) => {
      const deadline = wo.wo_due_date
        ? dayjs(wo.wo_due_date)
        : dayjs(wo.pi_date).add(wo.pi_lead_time, 'day');
      const today = dayjs().startOf('day');
      const daysLeft = deadline.startOf('day').diff(today, 'day');

      const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
      const machineryDept = departmentsList.find(d => String(d.label).toLowerCase().includes("machinery"));
      const isMachinery = !!(
        (emp && emp.employee_department && machineryDept &&
          String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).includes(Number(machineryDept.id))) ||
        (wo.wo_department_name && String(wo.wo_department_name).toLowerCase().includes('machinery'))
      );

      const matchedBom = boms.find(b => Number(b.bom_product_id) === Number(wo.product_id));
      const bomComponents = matchedBom && Array.isArray(matchedBom.components) ? matchedBom.components : [];

      const targetQty = Number(wo.wo_qty) || 1;
      let isCompleted = false;
      let progressPercent = 0;
      let completedComponentsCount = 0;
      let totalComponentsCount = 0;

      if (isMachinery && bomComponents.length > 0) {
        totalComponentsCount = bomComponents.length;
        const details = woDetailsMap[wo.wo_id] || [];
        const totalCompTarget = totalComponentsCount * targetQty;
        let totalCompCompleted = 0;
        let allDone = true;

        bomComponents.forEach((c: any) => {
          const compName = c.component_name;
          const compCompleted = details
            .filter((d: any) => {
              if (Number(d.wod_qty) <= 0) return false;
              const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
              return match && match[1] === compName;
            })
            .reduce((sum: number, d: any) => sum + Number(d.wod_qty), 0);

          totalCompCompleted += Math.min(compCompleted, targetQty);
          if (compCompleted >= targetQty) {
            completedComponentsCount++;
          } else {
            allDone = false;
          }
        });

        isCompleted = allDone;
        progressPercent = totalCompTarget > 0 ? (totalCompCompleted / totalCompTarget) * 100 : 0;
      } else {
        const completedQty = Number(wo.total_complete || 0);
        isCompleted = completedQty >= targetQty;
        progressPercent = Math.min((completedQty / targetQty) * 100, 100);
      }

      let borderColor = '#52c41a'; // Green (On Time)
      let glowColor = 'rgba(82, 196, 26, 0.18)';
      let badgeBg = 'rgba(82, 196, 26, 0.08)';
      let statusText = '';

      if (isCompleted) {
        if (wo.wo_done_on_time === 'Y') {
          borderColor = '#52c41a';
          glowColor = 'rgba(82, 196, 26, 0.18)';
          badgeBg = 'rgba(82, 196, 26, 0.08)';
          statusText = 'Completed (On Time)';
        } else if (wo.wo_done_on_time === 'N') {
          borderColor = '#ff4d4f';
          glowColor = 'rgba(255, 77, 79, 0.18)';
          badgeBg = 'rgba(255, 77, 79, 0.08)';
          statusText = 'Completed (Late)';
        } else {
          borderColor = '#52c41a';
          glowColor = 'rgba(82, 196, 26, 0.18)';
          badgeBg = 'rgba(82, 196, 26, 0.08)';
          statusText = 'Completed';
        }
      } else {
        statusText = `${daysLeft} days left`;
        if (daysLeft < 0) {
          borderColor = '#ff4d4f'; // Red (Overdue)
          glowColor = 'rgba(255, 77, 79, 0.18)';
          badgeBg = 'rgba(255, 77, 79, 0.08)';
          statusText = `${Math.abs(daysLeft)}d overdue`;
        } else if (daysLeft <= 3) {
          borderColor = '#faad14'; // Yellow (Due Soon)
          glowColor = 'rgba(250, 173, 20, 0.18)';
          badgeBg = 'rgba(250, 173, 20, 0.08)';
          statusText = daysLeft === 0 ? 'Due today' : `${daysLeft}d left`;
        }
      }

      return {
        pid_id: wo.wo_pid_detail_id,
        wo_id: wo.wo_id,
        pi_id: wo.wo_pi_id,
        pi_order_no: String(wo.pi_order_no),
        pi_date: wo.pi_date,
        pi_lead_time: wo.pi_lead_time,
        pi_created_by: wo.pi_created_by,
        ledger_company_name: wo.ledger_company_name,
        ledger_first_name: wo.ledger_first_name,
        ledger_last_name: wo.ledger_last_name,
        product_id: wo.product_id,
        product_name: wo.product_name,
        qty: Number(wo.wo_qty),
        employee_fname: wo.employee_fname,
        employee_lname: wo.employee_lname,
        wo_status: wo.wo_status,
        wo_number: wo.wo_number,
        wo_employee_id: wo.wo_employee_id,
        daysLeft,
        statusText,
        borderColor,
        glowColor,
        badgeBg,
        total_complete: Number(wo.total_complete || 0),
        wo_due_date: wo.wo_due_date,
        wo_done_on_time: wo.wo_done_on_time,
        isMachinery,
        isCompleted,
        progressPercent,
        completedComponentsCount,
        totalComponentsCount,
      };
    });
  }, [workOrders, pid_id, pi_id, productDetail, employees, departmentsList, boms, woDetailsMap]);

  // Keep selectedWO in sync with updated flatWorkOrders
  useEffect(() => {
    if (selectedWO) {
      const updated = flatWorkOrders.find(w => w.wo_id === selectedWO.wo_id);
      if (updated && (
        updated.isCompleted !== selectedWO.isCompleted ||
        updated.progressPercent !== selectedWO.progressPercent ||
        updated.statusText !== selectedWO.statusText ||
        updated.completedComponentsCount !== selectedWO.completedComponentsCount
      )) {
        setSelectedWO(updated);
      }
    }
  }, [flatWorkOrders]);

  // Apply Advanced Filtering (Department, User, Query Search)
  const filteredWorkOrders = useMemo(() => {
    return flatWorkOrders.filter((wo) => {
      // 1. User Filter
      if (selectedUser !== null && selectedUser !== undefined && wo.pi_created_by !== selectedUser) {
        return false;
      }

      // 2. Department Filter (based on assigned employee's department)
      if (selectedDept) {
        const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
        if (!emp || !emp.employee_department) return false;

        const deptObj = departmentsList.find(d => d.key === selectedDept);
        if (!deptObj) return false;

        const deptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
        if (!deptIds.includes(Number(deptObj.id))) {
          return false;
        }
      }

      // 3. Search Query Filter
      const customer = String(`${wo.ledger_company_name || ''} ${wo.ledger_first_name || ''} ${wo.ledger_last_name || ''}`).toLowerCase();
      const product = String(wo.product_name || '').toLowerCase();
      const piOrder = String(wo.pi_order_no || '').toLowerCase();
      const employee = String(`${wo.employee_fname || ''} ${wo.employee_lname || ''}`).toLowerCase();
      const woId = `wo-${wo.wo_id}`;
      const query = searchQuery.toLowerCase();

      return (
        customer.includes(query) ||
        product.includes(query) ||
        piOrder.includes(query) ||
        employee.includes(query) ||
        woId.includes(query) ||
        String(wo.wo_id).includes(query)
      );
    });
  }, [flatWorkOrders, selectedUser, selectedDept, searchQuery, departmentsList, employees]);

  // Group Work Orders by Department (based on assigned employee's department)
  const groupedWorkOrders = useMemo(() => {
    const groups: Record<string, any[]> = {};

    DEPARTMENTS.forEach(dept => {
      groups[dept.key] = [];
    });
    groups['unassigned'] = [];

    filteredWorkOrders.forEach(wo => {
      const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
      if (!emp || !emp.employee_department) {
        groups['unassigned'].push(wo);
        return;
      }

      const empDeptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
      const matchedDept = DEPARTMENTS.find(d => empDeptIds.includes(Number(d.id)));
      if (matchedDept) {
        groups[matchedDept.key].push(wo);
      } else {
        groups['unassigned'].push(wo);
      }
    });

    return groups;
  }, [filteredWorkOrders, DEPARTMENTS, employees]);

  /* ================= INJECT DYNAMIC HOVER STYLES ================= */
  useEffect(() => {
    const styleId = "track-work-order-cards-style-product-detail";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .track-bento-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 22px 24px !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        min-height: 190px !important;
        position: relative !important;
        overflow: hidden !important;
        transition: box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease !important;
        cursor: pointer !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.01) !important;
      }
      .track-bento-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background-color: var(--accent-color);
        transition: width 0.2s ease-out;
        z-index: 2;
        pointer-events: none;
      }
      /* Sheen sweep overlay */
      .track-bento-card::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 45%,
          ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.25)'} 50%,
          transparent 55%
        );
        transform: rotate(-45deg) translate(-75%, -75%);
        transition: transform 0.6s ease-out;
        pointer-events: none;
        z-index: 1;
      }
      .track-bento-card:hover {
        border-color: var(--accent-color) !important;
        box-shadow: 0 12px 28px -4px ${darkMode ? 'rgba(0, 0, 0, 0.55)' : 'rgba(148, 163, 184, 0.22)'}, 0 0 16px var(--accent-glow) !important;
      }
      .track-bento-card:hover::before {
        width: 6px;
      }
      .track-bento-card:hover::after {
        transform: rotate(-45deg) translate(75%, 75%);
      }
      .track-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--badge-bg) !important;
        color: var(--accent-color) !important;
        border: 1px solid var(--accent-color) !important;
        border-radius: 20px;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
      .ant-progress-text {
        color: ${customTextMain} !important;
        font-weight: 700 !important;
        font-size: 12px !important;
      }
    `;
  }, [customCardBg, customCardBorder, customTextMain, themeStyles, darkMode]);

  // Overall Product summary info
  const productName = productDetail?.product_name || (flatWorkOrders[0]?.product_name || `Product #${pid_id}`);
  const piOrderNo = productDetail?.pi_order_no || (flatWorkOrders[0]?.pi_order_no || pi_id);
  const totalTargetQty = productDetail?.total_qty ?? flatWorkOrders.reduce((sum, wo) => sum + wo.qty, 0);
  const totalReadyQty = productDetail?.ready_qty ?? flatWorkOrders.reduce((sum, wo) => sum + (wo.total_complete || 0), 0);
  const isDone = totalTargetQty > 0 && Number(totalReadyQty) >= Number(totalTargetQty);
  const percent = totalTargetQty > 0 ? Math.min(Math.round((Number(totalReadyQty) / Number(totalTargetQty)) * 100), 100) : 0;

  const deadline = productDetail?.pi_date
    ? dayjs(productDetail.pi_date).add(productDetail.pi_lead_time || 0, 'day')
    : (flatWorkOrders[0]?.wo_due_date ? dayjs(flatWorkOrders[0].wo_due_date) : dayjs());
  const daysLeft = deadline.startOf('day').diff(dayjs().startOf('day'), 'day');
  const tagColor = daysLeft < 0 ? 'red' : daysLeft <= 3 ? 'orange' : 'green';
  const statusText = daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Due today' : `${daysLeft}d left`;

  // Timber Issue Data for selected work order
  const selectedWoIssuedData = useMemo(() => {
    if (!selectedWO) return { issuedCft: 0, issuedAmount: 0, matchingTimber: [] };

    const woId = selectedWO.wo_id;
    const woNum = selectedWO.wo_number;
    const piId = selectedWO.pi_id || pi_id;

    // 1. Filter piDetail.issuedTimber
    const rawIssuedTimber = (piDetail?.issuedTimber || []);

    let matchingTimber = rawIssuedTimber.filter((it: any) => {
      if (it.work_order_id && Number(it.work_order_id) === Number(woId)) return true;
      if (it.im_master_order_no && (String(it.im_master_order_no) === String(woId) || String(it.im_master_order_no) === String(woNum))) return true;
      return false;
    });

    if (matchingTimber.length === 0) {
      matchingTimber = rawIssuedTimber.filter((it: any) => {
        if (it.pi_id && String(it.pi_id) === String(piId)) return true;
        if (it.im_master_pi_no && (String(it.im_master_pi_no) === String(piId) || String(it.im_master_pi_no) === String(selectedWO.pi_order_no))) return true;
        return false;
      });
    }

    // 2. Also check issueMasterList for timber issues matching this WO
    const matchingMaster = (issueMasterList || []).filter((im: any) => {
      if (im.im_master_order_no && (String(im.im_master_order_no) === String(woId) || String(im.im_master_order_no) === String(woNum))) return true;
      return false;
    });

    let totalCft = 0;
    let totalAmt = 0;

    matchingTimber.forEach((it: any) => {
      const cft = Number(it.cft || it.issued_cft || it.tiod_cft || 0);
      const rate = Number(it.rate || it.tiod_rate || it.price || 0);
      const amt = Number(it.amount || it.tiod_amount || (cft * rate) || 0);
      totalCft += cft;
      totalAmt += amt;
    });

    if (totalCft === 0 && matchingMaster.length > 0) {
      matchingMaster.forEach((im: any) => {
        totalAmt += Number(im.im_total || 0);
      });
    }

    return {
      issuedCft: parseFloat(totalCft.toFixed(4)),
      issuedAmount: parseFloat(totalAmt.toFixed(4)),
      matchingTimber
    };
  }, [selectedWO, piDetail, issueMasterList, pi_id]);

  // BOM CFT requirement per 1 piece of product
  const bomTimberPerPc = useMemo(() => {
    if (!selectedWO || !matchedBom) return 0;
    let totalCftForOneProduct = 0;

    (matchedBom.components || []).forEach((comp: any) => {
      const compQty = Number(comp.component_qty) || 1;
      (comp.woodRows || []).forEach((wRow: any) => {
        let rawWidthsObj = wRow.widths || wRow.bwr_widths || {};
        if (typeof rawWidthsObj === 'string') {
          try { rawWidthsObj = JSON.parse(rawWidthsObj); } catch { rawWidthsObj = {}; }
        }
        const woodMaster = woods.find((w: any) => Number(w.wood_id) === Number(wRow.bwr_wood_id));
        const length = woodMaster ? Number(woodMaster.wood_length) : 5.25;
        const thickness = woodMaster ? Number(woodMaster.wood_thickness) : 1.5;
        const widthQtyTotal = getWidthQtyTotal(rawWidthsObj);
        const unitCft = calculateCFT(widthQtyTotal, length, thickness);
        totalCftForOneProduct += unitCft * compQty;
      });
    });

    return parseFloat(totalCftForOneProduct.toFixed(3));
  }, [selectedWO, matchedBom, woods]);

  // Profit / Loss calculation for selected work order (based on requested vs completed total)
  const profitLossData = useMemo(() => {
    if (!selectedWO) return { expectedPcs: 0, requestedPcs: 0, readyPcs: 0, diffPcs: 0, percent: 0, isProfit: false, isLoss: false, isBalanced: true };

    const requestedPcs = Number(selectedWO.qty) || 1;

    let readyPcs = 0;
    if (selectedWO.isMachinery && bomComponents.length > 0) {
      const compCompletedList = bomComponents.map((c: any) => {
        const compName = c.component_name;
        return dailyDetails
          .filter(d => {
            if (Number(d.wod_qty) <= 0) return false;
            const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
            return match && match[1] === compName;
          })
          .reduce((sum, d) => sum + Number(d.wod_qty), 0);
      });
      readyPcs = compCompletedList.length > 0 ? Math.min(...compCompletedList) : 0;
    } else {
      readyPcs = dailyDetails
        .filter(d => Number(d.wod_qty) > 0)
        .reduce((sum, d) => sum + Number(d.wod_qty), 0);
    }

    const isDateExceeded = selectedWO.wo_done_on_time === 'N' || selectedWO.daysLeft < 0 || (
      selectedWO.wo_due_date
        ? dayjs(selectedWO.wo_due_date).startOf('day').isBefore(dayjs().startOf('day'))
        : (selectedWO.pi_date ? dayjs(selectedWO.pi_date).add(selectedWO.pi_lead_time || 0, 'day').startOf('day').isBefore(dayjs().startOf('day')) : false)
    );

    const diffPcs = readyPcs - requestedPcs;
    const percent = requestedPcs > 0 ? parseFloat(((diffPcs / requestedPcs) * 100).toFixed(1)) : 0;

    const isProfit = diffPcs > 0;
    const isLoss = diffPcs < 0 && isDateExceeded;
    const isBalanced = diffPcs === 0 && (readyPcs >= requestedPcs || isDateExceeded);
    const isPending = diffPcs < 0 && !isDateExceeded;

    return {
      expectedPcs: requestedPcs,
      requestedPcs,
      readyPcs,
      diffPcs,
      percent: Math.abs(percent),
      rawPercent: percent,
      isProfit,
      isLoss,
      isBalanced,
      isPending,
      isDateExceeded
    };
  }, [selectedWO, bomComponents, dailyDetails]);

  // Helper to calculate profit / loss for any work order (for download list & cards)
  const getWoProfitLoss = (wo: FlatWorkOrder) => {
    const requestedPcs = Number(wo.qty) || 1;
    const matchedBom = boms.find(b => Number(b.bom_product_id) === Number(wo.product_id));
    const bomComponents = matchedBom && Array.isArray(matchedBom.components) ? matchedBom.components : [];

    let readyPcs = 0;
    if (wo.isMachinery && bomComponents.length > 0) {
      const details = woDetailsMap[wo.wo_id] || [];
      const compCompletedList = bomComponents.map((c: any) => {
        const compName = c.component_name;
        return details
          .filter((d: any) => {
            if (Number(d.wod_qty) <= 0) return false;
            const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
            return match && match[1] === compName;
          })
          .reduce((sum: number, d: any) => sum + Number(d.wod_qty), 0);
      });
      readyPcs = compCompletedList.length > 0 ? Math.min(...compCompletedList) : 0;
    } else {
      readyPcs = Number(wo.total_complete || 0);
    }

    const isDateExceeded = wo.wo_done_on_time === 'N' || wo.daysLeft < 0 || (
      wo.wo_due_date
        ? dayjs(wo.wo_due_date).startOf('day').isBefore(dayjs().startOf('day'))
        : (wo.pi_date ? dayjs(wo.pi_date).add(wo.pi_lead_time || 0, 'day').startOf('day').isBefore(dayjs().startOf('day')) : false)
    );

    const diffPcs = readyPcs - requestedPcs;
    const percent = requestedPcs > 0 ? parseFloat(((diffPcs / requestedPcs) * 100).toFixed(1)) : 0;

    const isProfit = diffPcs > 0;
    const isLoss = diffPcs < 0 && isDateExceeded;
    const isBalanced = diffPcs === 0 && (readyPcs >= requestedPcs || isDateExceeded);
    const isPending = diffPcs < 0 && !isDateExceeded;

    return {
      requestedPcs,
      readyPcs,
      diffPcs,
      percent: Math.abs(percent),
      rawPercent: percent,
      isProfit,
      isLoss,
      isBalanced,
      isPending,
      isDateExceeded
    };
  };

  // Timber table data for View CFT Issue modal
  const timberTableData = useMemo(() => {
    if (!selectedWO || !matchedBom) return { rows: [], activeWidthKeys: [], totalReqCft: 0, totalIssuedCft: 0, totalAmt: 0 };

    const rows: any[] = [];
    const widthSet = new Set<string>();

    (matchedBom.components || []).forEach((comp: any, cIdx: number) => {
      (comp.woodRows || []).forEach((wRow: any, wIdx: number) => {
        let rawWidthsObj = wRow.widths || wRow.bwr_widths || {};
        if (typeof rawWidthsObj === 'string') {
          try { rawWidthsObj = JSON.parse(rawWidthsObj); } catch { rawWidthsObj = {}; }
        }
        const actualWidthsObj: { [key: string]: number } = {};
        let mixQtyFromWidth1 = 0;

        Object.entries(rawWidthsObj).forEach(([w, q]) => {
          if (String(w) === "1") {
            mixQtyFromWidth1 = Number(q) || 0;
          } else {
            actualWidthsObj[w] = Number(q) || 0;
            if (Number(q) > 0) widthSet.add(String(w));
          }
        });

        const woodMaster = woods.find((w) => Number(w.wood_id) === Number(wRow.bwr_wood_id));
        const woodTypeObj = woodTypes.find((wt) => Number(wt.wood_id) === Number(wRow.bwr_wood_type_id));

        const length = woodMaster ? Number(woodMaster.wood_length) : 5.25;
        const thickness = woodMaster ? Number(woodMaster.wood_thickness) : 1.5;
        const typeLabel = woodTypeObj?.wood_name
          ? woodTypeObj.wood_name.charAt(0).toUpperCase()
          : woodMaster?.wood_name || "S";

        const sizeWoodType = `${length}' x ${thickness}" (${typeLabel})`;
        const is24Inch = length === 2;
        const mixQty = is24Inch ? 24 : (mixQtyFromWidth1 || Number(wRow.mix) || Number(wRow.mix_qty) || 0);

        const compQty = Number(comp.component_qty) || 1;
        const multiplier = compQty * Number(selectedWO.qty);

        const widthQtyTotal = getWidthQtyTotal(rawWidthsObj);
        const unitCft = calculateCFT(widthQtyTotal, length, thickness);
        const totalCft = parseFloat((unitCft * multiplier).toFixed(4));
        const price = Number(wRow.bwr_rate) || 0;
        const totalAmount = parseFloat((totalCft * price).toFixed(4));

        const sumWidthPcs = Object.values(actualWidthsObj).reduce((sum: number, q: any) => sum + (Number(q) || 0), 0);
        const basePcs = is24Inch ? 24 : (sumWidthPcs > 0 ? sumWidthPcs : (mixQty || 100));
        const totalPcs = basePcs * multiplier;

        const scaledWidths = Object.fromEntries(
          Object.entries(actualWidthsObj).map(([w, q]) => [w, Number(q) * multiplier])
        );

        // Find matched timber issues from piDetail.issuedTimber
        const matchedIssues = (piDetail?.issuedTimber || []).filter((it: any) => {
          const itWoodId = Number(it.wood_id || it.tiod_wood_id || it.woodId);
          const itWoodTypeId = Number(it.wood_type_id || it.tiod_wood_type_id || it.woodTypeId);
          return itWoodId === Number(wRow.bwr_wood_id) && itWoodTypeId === Number(wRow.bwr_wood_type_id);
        });

        const issuedWidths: { [w: string]: number } = {};
        let totalIssuedCft = 0;

        matchedIssues.forEach((it: any) => {
          if (it.cft !== undefined && it.cft !== null && !isNaN(Number(it.cft))) {
            totalIssuedCft += Number(it.cft);
          } else if (it.issued_cft !== undefined && it.issued_cft !== null && !isNaN(Number(it.issued_cft))) {
            totalIssuedCft += Number(it.issued_cft);
          } else if (it.tiod_cft !== undefined && it.tiod_cft !== null && !isNaN(Number(it.tiod_cft))) {
            totalIssuedCft += Number(it.tiod_cft);
          }

          let rWidths = it.widths || it.tiod_widths || it.wood_widths;
          if (typeof rWidths === 'string') {
            try { rWidths = JSON.parse(rWidths); } catch { rWidths = null; }
          }
          if (rWidths && typeof rWidths === 'object') {
            Object.entries(rWidths).forEach(([w, q]) => {
              const numQ = Number(q) || 0;
              if (numQ > 0) {
                issuedWidths[String(w)] = (issuedWidths[String(w)] || 0) + numQ;
              }
            });
          }
        });

        if (totalIssuedCft === 0 && Object.keys(issuedWidths).length > 0) {
          totalIssuedCft = calculateCFT(getWidthQtyTotal(issuedWidths), length, thickness);
        }

        rows.push({
          key: `${cIdx}-${wIdx}`,
          size_wood_type: sizeWoodType,
          mix: mixQty > 0 ? mixQty * multiplier : (sumWidthPcs > 0 ? "" : 100 * multiplier),
          widths: scaledWidths,
          issued_widths: issuedWidths,
          cft: totalCft,
          issued_cft: parseFloat(totalIssuedCft.toFixed(4)),
          pcs: totalPcs,
          price: price,
          total_amount: totalAmount,
          wood_id: wRow.bwr_wood_id,
          wood_type_id: wRow.bwr_wood_type_id
        });
      });
    });

    const activeWidthKeys = Array.from(widthSet).sort((a, b) => Number(a) - Number(b));
    if (activeWidthKeys.length === 0) {
      activeWidthKeys.push("4", "5");
    }

    const totalReqCft = rows.reduce((acc, r) => acc + (r.cft || 0), 0);
    const totalIssuedCft = rows.reduce((acc, r) => acc + (r.issued_cft || 0), 0);
    const totalAmt = rows.reduce((acc, r) => acc + (r.total_amount || 0), 0);

    return { rows, activeWidthKeys, totalReqCft, totalIssuedCft, totalAmt };
  }, [selectedWO, matchedBom, woods, woodTypes, piDetail]);

  // Export timber table to Excel
  const handleExportTimberExcel = () => {
    if (!selectedWO || timberTableData.rows.length === 0) return;
    const aoa: any[][] = [];
    aoa.push([`TIMBER ISSUE TRACKING - WO #${selectedWO.wo_number || selectedWO.wo_id} (${selectedWO.product_name})`]);
    aoa.push([]);
    const headers = ["PO No.", "Size/Wood Type", "Mix"];
    timberTableData.activeWidthKeys.forEach((w) => headers.push(`${w}"`));
    headers.push("Req. CFT", "Issued CFT", "Pcs", "Price", "Total Amt");
    aoa.push(headers);

    timberTableData.rows.forEach((r, idx) => {
      const poNo = `Exp. #${selectedWO.pi_order_no}/${String(idx + 1).padStart(2, '0')}`;
      const rowVals = [
        poNo,
        r.size_wood_type,
        r.mix || "",
        ...timberTableData.activeWidthKeys.map((w) => {
          const req = r.widths?.[w];
          const iss = r.issued_widths?.[w] || 0;
          if (iss > 0 && req) return `${req}/${iss}`;
          if (iss > 0) return `${iss}`;
          return req || "";
        }),
        r.cft,
        r.issued_cft,
        r.pcs,
        r.price,
        r.total_amount
      ];
      aoa.push(rowVals);
    });

    aoa.push([]);
    aoa.push(["Grand Total", "", "", ...timberTableData.activeWidthKeys.map(() => ""), timberTableData.totalReqCft.toFixed(4), timberTableData.totalIssuedCft.toFixed(4), "", "", timberTableData.totalAmt.toFixed(4)]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, "Timber Issue");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), `Timber_Issue_WO_${selectedWO.wo_number || selectedWO.wo_id}.xlsx`);
  };

  // Matching Issue Master vouchers
  const matchingIssueMasterRecords = useMemo(() => {
    if (!selectedWO) return [];
    const woId = selectedWO.wo_id;
    const woNum = selectedWO.wo_number;
    const piId = selectedWO.pi_id || pi_id;

    return (issueMasterList || []).filter((im: any) => {
      if (im.im_master_order_no && (String(im.im_master_order_no) === String(woId) || String(im.im_master_order_no) === String(woNum))) return true;
      if (im.work_order && (String(im.work_order.work_order_no) === String(woNum) || String(im.work_order.wo_id) === String(woId))) return true;
      if (im.im_master_pi_no && (String(im.im_master_pi_no) === String(piId) || String(im.im_master_pi_no) === String(selectedWO.pi_order_no))) return true;
      return false;
    });
  }, [selectedWO, issueMasterList, pi_id]);

  const renderWorkOrderCard = (wo: FlatWorkOrder, index: number, isCompleted: boolean) => {
    const progressPercent = wo.progressPercent !== undefined
      ? wo.progressPercent
      : Math.min(((wo.total_complete || 0) / (wo.qty || 1)) * 100, 100);

    const cardAccentColor = isCompleted
      ? (wo.wo_done_on_time === 'N' ? '#ff4d4f' : '#52c41a')
      : '#1890ff';
    const cardStageLabel = isCompleted
      ? (wo.wo_done_on_time === 'Y'
        ? 'Completed (On Time)'
        : (wo.wo_done_on_time === 'N' ? 'Completed (Late)' : 'Completed')
      )
      : 'Active';
    const accentGlow = isCompleted
      ? (wo.wo_done_on_time === 'N' ? 'rgba(255, 77, 79, 0.18)' : 'rgba(82, 196, 26, 0.18)')
      : 'rgba(24, 144, 255, 0.18)';

    return (
      <div
        key={wo.wo_id}
        className="track-bento-card"
        onClick={() => openDetailsModal(wo)}
        style={{
          '--accent-color': cardAccentColor,
          '--accent-glow': accentGlow,
          '--badge-bg': `${cardAccentColor}15`,
          animationDelay: `${index * 40}ms`
        } as React.CSSProperties}
      >
        {/* Section 1: Work Order ID & Status Badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', margin: 0, color: customTextMain, letterSpacing: '-0.02em' }}>
              {wo.wo_number || `WO-${wo.wo_id}`}
            </h3>
            <span style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.05em' }}>
              PI ORDER #{wo.pi_order_no}
            </span>
          </div>

          <div className="track-badge">
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: wo.borderColor, display: 'inline-block' }} />
            {wo.statusText}
          </div>
        </div>

        {/* Section 2: Product Name & Quantity Details */}
        <div style={{ margin: '14px 0 10px 0', width: '100%' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 4px 0', color: customTextMain }}>
            {wo.product_name}
          </h4>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
            <div style={{ color: customTextSub }}>
              Qty: <strong style={{ color: customTextMain, fontSize: '13px' }}>{wo.qty} pcs</strong>
            </div>
            {wo.isMachinery && (wo.totalComponentsCount ?? 0) > 0 ? (
              <div style={{ color: customTextSub }}>
                Components: <strong style={{ color: isCompleted ? '#52c41a' : '#1890ff', fontSize: '13px' }}>
                  {wo.completedComponentsCount || 0} / {wo.totalComponentsCount} done
                </strong>
              </div>
            ) : !wo.isMachinery ? (
              <div style={{ color: customTextSub }}>
                Complete: <strong style={{ color: '#52c41a', fontSize: '13px' }}>{wo.total_complete || 0} pcs</strong>
              </div>
            ) : null}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', marginTop: '6px' }}>
            <div style={{ color: customTextSub }}>
              Assigned To: <strong style={{ color: customTextMain }}>{wo.employee_fname ? `${wo.employee_fname} ${wo.employee_lname || ''}`.trim() : 'Unassigned'}</strong>
            </div>
            {wo.isMachinery && (
              (() => {
                const pl = getWoProfitLoss(wo);
                if (pl.isProfit) {
                  return <Tag color="success" style={{ fontSize: '11px', margin: 0, padding: '0 6px', fontWeight: 700 }}>+{pl.diffPcs} pcs (+{pl.percent}%) Profit</Tag>;
                } else if (pl.isLoss && pl.readyPcs > 0) {
                  return <Tag color="error" style={{ fontSize: '11px', margin: 0, padding: '0 6px', fontWeight: 700 }}>{pl.diffPcs} pcs ({pl.percent}%) Loss</Tag>;
                } else if (pl.isBalanced && pl.readyPcs > 0) {
                  return <Tag color="blue" style={{ fontSize: '11px', margin: 0, padding: '0 6px', fontWeight: 700 }}>0 pcs (0%)</Tag>;
                }
                return null;
              })()
            )}
          </div>
        </div>

        {/* Section 3: Production Progress Bar */}
        <div style={{ width: '100%', margin: '12px 0 4px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: '700', color: customTextSub, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '6px' }}>
            <span>Status: <strong style={{ color: cardAccentColor, textTransform: 'none' }}>{cardStageLabel}</strong></span>
            <span>{Math.round(progressPercent)}%</span>
          </div>

          <Progress
            percent={Math.min(100, Math.round(progressPercent))}
            strokeColor={{
              '0%': '#1890ff',
              '100%': cardAccentColor,
            }}
            trailColor={darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
            showInfo={false}
            size="small"
            style={{ margin: 0 }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 20px', minHeight: '90vh', backgroundColor: customLayoutBg, transition: 'background-color 0.3s ease' }}>

      {/* ── Breadcrumb ── */}
      <Breadcrumb style={{ marginBottom: 20 }}>
        <Breadcrumb.Item href="/">
          <Icons.HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate("/workflow")}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            Workflow
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate(`/workflow/${pi_id}`)}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            PI #{piOrderNo}
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{productName}</Breadcrumb.Item>
      </Breadcrumb>

      {/* ── Top Product Summary Card ── */}
      <div
        style={{
          border: `2px solid ${isDone ? "#52c41a" : customCardBorder}`,
          borderRadius: 12,
          padding: "18px 24px",
          background: customCardBg,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: customTextSub }}>Product</div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: customTextMain,
            }}
          >
            {productName}
          </div>
          <div style={{ fontSize: 12, color: customTextSub }}>
            PI #{piOrderNo}
          </div>
        </div>

        <div style={{ width: 1, height: 48, background: darkMode ? "rgba(255,255,255,0.08)" : "#f0f0f0", flexShrink: 0 }} />

        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: customTextSub }}>Total Target QTY</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: customTextMain }}>
              {totalTargetQty} pcs
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: customTextSub }}>Ready / Completed QTY</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: isDone ? "#52c41a" : "#1677ff",
              }}
            >
              {totalReadyQty} pcs
              {isDone && (
                <Icons.CheckCircleFilled
                  style={{ marginLeft: 6, color: "#52c41a" }}
                />
              )}
            </div>
          </div>
        </div>

        <div style={{ width: 1, height: 48, background: darkMode ? "rgba(255,255,255,0.08)" : "#f0f0f0", flexShrink: 0 }} />

        <div style={{ minWidth: 200 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              marginBottom: 4,
            }}
          >
            <span style={{ color: customTextSub }}>Overall Completion</span>
            <span style={{ fontWeight: 700, color: isDone ? "#52c41a" : customTextMain }}>
              {percent}%
            </span>
          </div>
          <Progress
            percent={percent}
            strokeColor={isDone ? "#52c41a" : "#1677ff"}
            trailColor={darkMode ? "rgba(255,255,255,0.08)" : "#f0f0f0"}
            size="small"
          />
        </div>

        <div style={{ width: 1, height: 48, background: darkMode ? "rgba(255,255,255,0.08)" : "#f0f0f0", flexShrink: 0 }} />

        <div>
          <div style={{ fontSize: 11, color: customTextSub }}>Deadline</div>
          <Tag color={tagColor} style={{ fontWeight: 600, fontSize: 12 }}>
            {statusText}
          </Tag>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: '28px',
        paddingBottom: '20px',
        borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
      }}>
        {/* Department Filter */}
        <Select
          size="large"
          placeholder="Filter by Department..."
          allowClear
          value={selectedDept}
          onChange={(val) => setSelectedDept(val)}
          style={{ width: '220px' }}
        >
          {DEPARTMENTS.map(d => (
            <Select.Option key={d.key} value={d.key}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: d.color }} />
                {d.label}
              </span>
            </Select.Option>
          ))}
        </Select>

        {/* User Filter */}
        <Select
          size="large"
          placeholder="Filter by Creator..."
          allowClear
          value={selectedUser}
          onChange={(val) => setSelectedUser(val)}
          style={{ width: '220px' }}
        >
          {users.map(u => (
            <Select.Option key={u.user_id} value={u.user_id}>
              {u.user_name} ({u.user_email})
            </Select.Option>
          ))}
        </Select>

        {/* Query Wise Search */}
        <Input
          size="large"
          placeholder="Search by WO #, client, employee..."
          prefix={<Icons.SearchOutlined style={{ color: customTextSub }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{
            width: '300px',
            borderRadius: '6px',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'}`,
            background: customCardBg,
            color: customTextMain,
            fontSize: '13px'
          }}
        />

        {/* Download Work Order Button */}
        <Button
          size="large"
          type="primary"
          icon={<Icons.DownloadOutlined />}
          onClick={() => {
            setSelectedWOIds([]);
            setDownloadModalVisible(true);
          }}
          style={{
            borderRadius: '6px',
            fontWeight: 600
          }}
        >
          Download Work Order
        </Button>

        {/* Clear Filters Button */}
        <Button
          size="large"
          icon={<Icons.ClearOutlined />}
          onClick={handleClearFilters}
          disabled={!selectedDept && selectedUser === null && !searchQuery}
          style={{
            borderRadius: '6px',
            fontWeight: 600
          }}
        >
          Clear Filters
        </Button>
      </div>

      {/* CORE CARDS CONTENT */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Spin size="large" />
        </div>
      ) : filteredWorkOrders.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '100%' }}>
          {DEPARTMENTS.map(dept => {
            const list = groupedWorkOrders[dept.key] || [];
            if (list.length === 0) return null;

            return (
              <div key={dept.key} style={{
                padding: '24px',
                background: darkMode ? 'rgba(30, 41, 59, 0.25)' : 'rgba(255, 255, 255, 0.45)',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                borderRadius: '16px',
              }}>
                {/* Department Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  borderBottom: `2px solid ${dept.color}25`,
                  paddingBottom: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: dept.color,
                      boxShadow: `0 0 8px ${dept.color}`
                    }} />
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      margin: 0,
                      color: customTextMain,
                      letterSpacing: '-0.01em'
                    }}>
                      {dept.label} Department
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      background: `${dept.color}15`,
                      color: dept.color,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {list.length} {list.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>

                {/* Cards Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '24px',
                    width: '100%',
                  }}
                >
                  {list.map((wo, index) => {
                    const isCompleted = wo.isCompleted ?? (Number(wo.total_complete || 0) >= Number(wo.qty));
                    return renderWorkOrderCard(wo, index, isCompleted);
                  })}
                </div>
              </div>
            );
          })}

          {/* Unassigned section at the end */}
          {(() => {
            const list = groupedWorkOrders['unassigned'] || [];
            if (list.length === 0) return null;

            return (
              <div key="unassigned" style={{
                padding: '24px',
                background: darkMode ? 'rgba(30, 41, 59, 0.25)' : 'rgba(255, 255, 255, 0.45)',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                borderRadius: '16px',
              }}>
                {/* Department Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  borderBottom: `2px solid #8c8c8c25`,
                  paddingBottom: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#8c8c8c',
                      boxShadow: `0 0 8px #8c8c8c`
                    }} />
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      margin: 0,
                      color: customTextMain,
                      letterSpacing: '-0.01em'
                    }}>
                      Unassigned Department
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      background: `#8c8c8c15`,
                      color: '#8c8c8c',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {list.length} {list.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>

                {/* Cards Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '24px',
                    width: '100%',
                  }}
                >
                  {list.map((wo, index) => {
                    const isCompleted = wo.isCompleted ?? (Number(wo.total_complete || 0) >= Number(wo.qty));
                    return renderWorkOrderCard(wo, index, isCompleted);
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        /* EMPTY STATE */
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: customTextSub, fontSize: '15px' }}>
                {flatWorkOrders.length > 0
                  ? "No work orders match the selected filters."
                  : "No work orders found for this product."}
              </span>
            }
          />
        </div>
      )}

      {/* 1. Daily Completion Details Modal */}
      <Modal
        title={
          <span style={{ fontSize: '18px', fontWeight: '800', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            Production Entries - {selectedWO?.wo_number || `WO-${selectedWO?.wo_id}`}
          </span>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={850}
        destroyOnHidden
      >
        {selectedWO && (
          <div style={{ marginTop: '16px' }}>
            {/* Header info / Above Card */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              padding: '16px 20px',
              background: darkMode ? '#1e293b' : '#f8fafc',
              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
              borderRadius: '10px',
              gap: '16px'
            }}>
              {/* Left Column Section */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.04em' }}>W/O ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: customTextMain }}>
                    {selectedWO.wo_number || `WO-${selectedWO.wo_id}`}
                  </div>
                  <div style={{ fontSize: '11px', color: customTextSub, marginTop: '2px' }}>
                    PI #{selectedWO.pi_order_no}
                  </div>
                </div>

                <div style={{ width: 1, height: 36, background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />

                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.04em' }}>WORK ORDER ISSUE DATE</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: customTextMain, marginTop: '2px' }}>
                    {dayjs(selectedWO.pi_date).format('DD-MMM-YYYY')}
                  </div>
                </div>

                <div style={{ width: 1, height: 36, background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />

                {/* Two columns after work order issue date: Issued CFT & Amount of CFT */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.04em' }}>ISSUED CFT</div>
                  <div style={{ fontSize: '14px', fontWeight: '800', color: selectedWoIssuedData.issuedCft > 0 ? '#10b981' : customTextMain, marginTop: '2px' }}>
                    {selectedWoIssuedData.issuedCft > 0 ? `${selectedWoIssuedData.issuedCft.toFixed(4)} cft` : '0.00 cft'}
                  </div>
                </div>

                <div style={{ width: 1, height: 36, background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />

                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.04em' }}>AMOUNT OF CFT</div>
                  <div style={{ fontSize: '14px', fontWeight: '800', color: selectedWoIssuedData.issuedAmount > 0 ? '#06b6d4' : customTextMain, marginTop: '2px' }}>
                    ₹ {selectedWoIssuedData.issuedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>

                {selectedWO.isMachinery && (
                  <>
                    <div style={{ width: 1, height: 36, background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.04em' }}>PROFIT & LOSS</div>
                      <div style={{ marginTop: '2px' }}>
                        {profitLossData.isProfit ? (
                          <Tag color="success" style={{ fontWeight: 800, margin: 0 }}>
                            +{profitLossData.diffPcs} pcs (+{profitLossData.percent}% Profit)
                          </Tag>
                        ) : profitLossData.isLoss ? (
                          <Tag color="error" style={{ fontWeight: 800, margin: 0 }}>
                            {profitLossData.diffPcs} pcs ({profitLossData.percent}% Loss)
                          </Tag>
                        ) : profitLossData.isBalanced && profitLossData.readyPcs > 0 ? (
                          <Tag color="blue" style={{ fontWeight: 800, margin: 0 }}>0 pcs (0% Balanced)</Tag>
                        ) : (
                          <Tag color="default" style={{ fontWeight: 800, margin: 0 }}>
                            Date Not Exceeded ({selectedWO.daysLeft === 0 ? 'Due today' : `${selectedWO.daysLeft}d left`})
                          </Tag>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Right Column Section: Product Name above, Status below, and View CFT Issue button */}
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                {/* Product name move above */}
                <div style={{ fontSize: '15px', fontWeight: '800', color: customTextMain }}>
                  {selectedWO.product_name}
                </div>
                {/* Status below */}
                <div>
                  <span className="track-badge" style={{
                    '--accent-color': selectedWO.borderColor,
                    '--badge-bg': selectedWO.badgeBg
                  } as React.CSSProperties}>{selectedWO.statusText}</span>
                </div>
                {/* Add new button view cft issue */}
                <Button
                  type="primary"
                  size="small"
                  icon={<Icons.FileTextOutlined />}
                  onClick={() => setTrackIssueModalVisible(true)}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: '#0284c7',
                    borderColor: '#0284c7',
                    fontWeight: 600,
                    marginTop: '2px'
                  }}
                >
                  View CFT Issue
                </Button>
              </div>
            </div>

            {/* Component Progress & Profit/Loss Section (For Machinery) */}
            {showComponentDropdown && bomComponents.length > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '16px 20px',
                background: darkMode ? 'rgba(30, 41, 59, 0.4)' : '#f8fafc',
                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'}`,
                borderRadius: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: customTextMain }}>
                    Component Completion & Profit / Loss Status
                  </div>
                  <span style={{ fontSize: '12px', color: customTextSub }}>
                    Requested per component: <strong>{selectedWO.qty} pcs</strong>
                  </span>
                </div>

                <Table
                  dataSource={bomComponents.map((c: any, index: number) => {
                    const compName = c.component_name;
                    console.log("c data", c)
                    const compCompleted = dailyDetails
                      .filter(d => {
                        if (Number(d.wod_qty) <= 0) return false;
                        const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
                        return match && match[1] === compName;
                      })
                      .reduce((sum, d) => sum + Number(d.wod_qty), 0);

                    const target = selectedWO.qty * c.component_qty;
                    const compDiff = compCompleted - target;
                    const compPercent = target > 0 ? parseFloat(((compDiff / target) * 100).toFixed(1)) : 0;
                    const progressPercent = target > 0 ? Math.round((compCompleted / target) * 100) : 0;

                    return {
                      key: compName || index,
                      component_name: compName,
                      target_qty: target,
                      completed_qty: compCompleted,
                      diff: compDiff,
                      diffPercent: compPercent,
                      progressPercent
                    };
                  })}
                  rowKey="key"
                  pagination={false}
                  size="small"
                  bordered
                  style={{ background: customCardBg }}
                  columns={[
                    {
                      title: 'Sr',
                      key: 'sr',
                      width: 50,
                      align: 'center',
                      render: (_, __, idx) => idx + 1
                    },
                    {
                      title: 'Component Name',
                      dataIndex: 'component_name',
                      key: 'component_name',
                      render: (text) => (
                        <span style={{ fontWeight: 700, color: darkMode ? '#38bdf8' : '#0284c7' }}>
                          {text}
                        </span>
                      )
                    },
                    {
                      title: 'Requested Qty',
                      dataIndex: 'target_qty',
                      key: 'target_qty',
                      width: 130,
                      align: 'center',
                      render: (qty) => `${qty} pcs`
                    },
                    {
                      title: 'Completed Qty',
                      dataIndex: 'completed_qty',
                      key: 'completed_qty',
                      width: 140,
                      align: 'center',
                      render: (qty, record: any) => (
                        <strong style={{ color: qty >= record.target_qty ? '#52c41a' : '#1890ff', fontSize: '13px' }}>
                          {qty} pcs
                        </strong>
                      )
                    },
                    {
                      title: 'Progress',
                      dataIndex: 'progressPercent',
                      key: 'progressPercent',
                      width: 180,
                      render: (pct) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Progress
                            percent={Math.min(pct, 100)}
                            size="small"
                            strokeColor={pct >= 100 ? '#52c41a' : '#1890ff'}
                            showInfo={false}
                            style={{ flex: 1, margin: 0 }}
                          />
                          <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '38px', textAlign: 'right' }}>
                            {pct}%
                          </span>
                        </div>
                      )
                    },
                    {
                      title: 'Profit / Loss',
                      key: 'profit_loss',
                      width: 200,
                      align: 'center',
                      render: (_, record: any) => {
                        const isDateExceeded = selectedWO.daysLeft < 0 || selectedWO.wo_done_on_time === 'N';
                        if (record.diff > 0) {
                          return (
                            <Tag color="success" style={{ fontWeight: 700, margin: 0 }}>
                              +{record.diff} pcs (+{record.diffPercent}%) Profit
                            </Tag>
                          );
                        } else if (record.diff < 0) {
                          if (isDateExceeded) {
                            return (
                              <Tag color="error" style={{ fontWeight: 700, margin: 0 }}>
                                {record.diff} pcs ({Math.abs(record.diffPercent)}%) Loss
                              </Tag>
                            );
                          }
                          return (
                            <Tag color="processing" style={{ fontWeight: 700, margin: 0 }}>
                              In Progress ({Math.abs(record.diff)} pcs pending)
                            </Tag>
                          );
                        }
                        return (
                          <Tag color="blue" style={{ fontWeight: 700, margin: 0 }}>
                            0 pcs (0% Balanced)
                          </Tag>
                        );
                      }
                    }
                  ]}
                  summary={(pageData) => {
                    const totalReq = pageData.reduce((acc, r: any) => acc + (r.target_qty || 0), 0);
                    const totalComp = pageData.reduce((acc, r: any) => acc + (r.completed_qty || 0), 0);
                    const totalDiff = totalComp - totalReq;
                    const totalDiffPct = totalReq > 0 ? parseFloat(((totalDiff / totalReq) * 100).toFixed(1)) : 0;

                    return (
                      <Table.Summary.Row style={{ background: darkMode ? '#1e293b' : '#fafafa', fontWeight: 700 }}>
                        <Table.Summary.Cell index={0} align="center">Total</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>Overall Summary</Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center">{totalReq} pcs</Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="center">
                          <span style={{ color: totalComp >= totalReq ? '#52c41a' : '#1890ff' }}>
                            {totalComp} pcs
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Progress
                              percent={totalReq > 0 ? Math.min(Math.round((totalComp / totalReq) * 100), 100) : 0}
                              size="small"
                              strokeColor={totalComp >= totalReq ? '#52c41a' : '#1890ff'}
                              showInfo={false}
                              style={{ flex: 1, margin: 0 }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '38px', textAlign: 'right' }}>
                              {totalReq > 0 ? Math.round((totalComp / totalReq) * 100) : 0}%
                            </span>
                          </div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="center">
                          {(() => {
                            const isDateExceeded = selectedWO.daysLeft < 0 || selectedWO.wo_done_on_time === 'N';
                            if (totalDiff > 0) {
                              return (
                                <Tag color="success" style={{ fontWeight: 700, margin: 0 }}>
                                  +{totalDiff} pcs (+{totalDiffPct}%) Profit
                                </Tag>
                              );
                            } else if (totalDiff < 0) {
                              if (isDateExceeded) {
                                return (
                                  <Tag color="error" style={{ fontWeight: 700, margin: 0 }}>
                                    {totalDiff} pcs ({Math.abs(totalDiffPct)}%) Loss
                                  </Tag>
                                );
                              }
                              return (
                                <Tag color="processing" style={{ fontWeight: 700, margin: 0 }}>
                                  Date Not Exceeded ({Math.abs(totalDiff)} pcs pending)
                                </Tag>
                              );
                            }
                            return (
                              <Tag color="blue" style={{ fontWeight: 700, margin: 0 }}>
                                0 pcs (0% Balanced)
                              </Tag>
                            );
                          })()}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </div>
            )}

            {/* Table Upper Part Info */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`
            }}>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>
                <span style={{ color: '#52c41a' }}>{selectedWO.product_name} - Total Pcs {selectedWO.qty}</span>
                {!selectedWO.isMachinery ? (
                  <span style={{ color: '#ff4d4f' }}> ({dailyDetails.filter(d => Number(d.wod_qty) > 0).reduce((sum, d) => sum + Number(d.wod_qty), 0)} completed pcs)</span>
                ) : (
                  <span style={{ color: selectedWO.isCompleted ? '#52c41a' : '#1890ff' }}>
                    {' '}({selectedWO.completedComponentsCount || 0}/{selectedWO.totalComponentsCount || 0} components done - {Math.round(selectedWO.progressPercent || 0)}%)
                  </span>
                )}
              </div>
              <Button
                type="primary"
                icon={<Icons.PlusOutlined />}
                onClick={handleOpenAdd}
                style={{ borderRadius: '6px' }}
              >
                Log Entry
              </Button>
            </div>

            {/* Table Column Details */}
            <Table
              dataSource={dailyDetails}
              rowKey="wod_id"
              loading={loadingDetails}
              pagination={false}
              size="small"
              bordered
              style={{ background: customCardBg }}
              columns={[
                {
                  title: 'Sr',
                  key: 'sr',
                  width: 60,
                  align: 'center',
                  render: (_, __, index) => index + 1
                },
                {
                  title: 'Date and time',
                  dataIndex: 'wod_created_on',
                  key: 'wod_created_on',
                  render: (val) => dayjs(val).format('DD-MMM-YYYY hh:mm A')
                },
                ...(showComponentDropdown ? [{
                  title: 'Component',
                  key: 'wod_component',
                  render: (_: any, record: any) => {
                    const match = String(record.wod_remarks || '').match(/^\[(.*?)\]/);
                    return match ? (
                      <span style={{
                        background: darkMode ? 'rgba(6, 182, 212, 0.2)' : '#e0f2fe',
                        color: darkMode ? '#38bdf8' : '#0284c7',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: '600',
                        fontSize: '12px'
                      }}>
                        {match[1]}
                      </span>
                    ) : '-';
                  }
                }] : []),
                {
                  title: 'No of pcs',
                  dataIndex: 'wod_qty',
                  key: 'wod_qty',
                  render: (qty) => {
                    const isPositive = qty > 0;
                    return (
                      <span style={{ fontWeight: '700', color: isPositive ? '#52c41a' : '#ff4d4f' }}>
                        {isPositive ? `+${qty}` : qty} pcs
                      </span>
                    );
                  }
                },
                {
                  title: 'Approve by (created By)',
                  dataIndex: 'approved_by_name',
                  key: 'approved_by_name',
                  render: (val) => val || 'System'
                },
                {
                  title: 'Remarks',
                  dataIndex: 'wod_remarks',
                  key: 'wod_remarks',
                  render: (val) => {
                    if (!val) return '-';
                    const cleaned = String(val).replace(/^\[(.*?)\]\s*/, '');
                    return cleaned || '-';
                  }
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 120,
                  align: 'center',
                  render: (_, record) => (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        icon={<Icons.EditOutlined />}
                        onClick={() => handleOpenEdit(record)}
                      />
                      <Popconfirm
                        title="Delete this daily log entry?"
                        onConfirm={() => handleDeleteDetail(record.wod_id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          size="small"
                          danger
                          icon={<Icons.DeleteOutlined />}
                        />
                      </Popconfirm>
                    </div>
                  )
                }
              ]}
              summary={() => {
                const totalReady = dailyDetails.filter(d => Number(d.wod_qty) > 0).reduce((sum, d) => sum + Number(d.wod_qty), 0);
                const totalLoss = dailyDetails.reduce((sum, d) => sum + (d.wod_qty < 0 ? Math.abs(d.wod_qty) : Number(d.wod_loss_qty || 0)), 0);
                return (
                  <Table.Summary.Row style={{ background: darkMode ? '#1e293b' : '#fafafa', fontWeight: 700 }}>
                    <Table.Summary.Cell index={0} align="center">Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    {showComponentDropdown && <Table.Summary.Cell index={2} />}
                    <Table.Summary.Cell index={showComponentDropdown ? 3 : 2}>
                      <span style={{ color: '#52c41a' }}>{totalReady} pcs</span>
                      {totalLoss > 0 && (
                        <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: '2px' }}>Total Rejection: -{totalLoss} pcs</div>
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={showComponentDropdown ? 4 : 3} />
                    <Table.Summary.Cell index={showComponentDropdown ? 5 : 4} />
                    <Table.Summary.Cell index={showComponentDropdown ? 6 : 5} />
                  </Table.Summary.Row>
                );
              }}
            />
          </div>
        )}
      </Modal>

      {/* 2. Add / Edit Production Entry Modal */}
      <Modal
        title={
          <span style={{ fontWeight: '700', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            {editingDetail ? 'Edit Production Entry' : 'Log Production Progress'}
          </span>
        }
        open={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        onOk={() => form.submit()}
        okText="Save"
        cancelText="Cancel"
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveDetail}
          style={{ marginTop: '16px' }}
        >
          {showComponentDropdown && (
            <Form.Item
              name="wod_component"
              label="Select Component"
              rules={[{ required: true, message: 'Please select a component' }]}
            >
              <Select
                placeholder="Choose component to log progress..."
                style={{ width: '100%' }}
                options={bomComponents.map((c: any) => ({
                  label: c.component_name,
                  value: c.component_name
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="wod_qty"
            label="Number of Pieces"
            rules={[
              { required: true, message: 'Please enter quantity' },
              {
                validator: (_, value) => {
                  if (value === 0) {
                    return Promise.reject(new Error('Quantity cannot be zero'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            extra="Positive value = Completed Pieces. Negative value = Rejected Pieces."
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g. 15 or -3"
            />
          </Form.Item>

          <Form.Item
            name="wod_created_on"
            label="Date & Time"
            rules={[{ required: true, message: 'Please select date & time' }]}
            extra={!editingDetail ? "Automatically set to current date & time on entry." : undefined}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              disabled={!editingDetail}
            />
          </Form.Item>

          <Form.Item
            name="wod_remarks"
            label="Remarks / Notes"
          >
            <Input.TextArea rows={3} placeholder="Optional details..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* 3. Download Select Work Orders Modal */}
      <Modal
        title={
          <span style={{ fontSize: '18px', fontWeight: '800', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            Download Work Order PDFs
          </span>
        }
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        width={750}
        footer={[
          <Button key="cancel" onClick={() => setDownloadModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<Icons.DownloadOutlined />}
            loading={downloadingPdf}
            onClick={handleDownloadSelectedPDF}
            disabled={selectedWOIds.length === 0}
          >
            Download Selected ({selectedWOIds.length})
          </Button>
        ]}
      >
        <div style={{ margin: '8px 0 16px 0' }}>
          Select the work orders you want to download. A single consolidated PDF will be generated containing one page per selected work order.
        </div>

        <Table
          dataSource={flatWorkOrders}
          rowKey="wo_id"
          pagination={{ pageSize: 5 }}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedWOIds,
            onChange: (keys) => setSelectedWOIds(keys as number[])
          }}
          columns={[
            {
              title: 'WO ID',
              dataIndex: 'wo_id',
              key: 'wo_id',
              render: (id) => `WO-${id}`
            },
            {
              title: 'PI Order #',
              dataIndex: 'pi_order_no',
              key: 'pi_order_no'
            },
            {
              title: 'Product',
              dataIndex: 'product_name',
              key: 'product_name'
            },
            {
              title: 'Target Qty',
              dataIndex: 'qty',
              key: 'qty',
              render: (qty) => `${qty} pcs`
            },
            {
              title: 'Complete',
              render: (_, record: FlatWorkOrder) => {
                if (record.isMachinery && (record.totalComponentsCount ?? 0) > 0) {
                  return `${record.completedComponentsCount || 0}/${record.totalComponentsCount} comps (${Math.round(record.progressPercent || 0)}%)`;
                }
                return `${record.total_complete || 0} pcs`;
              }
            },
            {
              title: 'Profit / Loss',
              key: 'profit_loss',
              render: (_, record: FlatWorkOrder) => {
                if (!record.isMachinery) return '-';
                const pl = getWoProfitLoss(record);
                if (pl.isProfit) {
                  return <Tag color="success" style={{ fontWeight: 700 }}>+{pl.diffPcs} pcs (+{pl.percent}%)</Tag>;
                } else if (pl.isLoss) {
                  return <Tag color="error" style={{ fontWeight: 700 }}>{pl.diffPcs} pcs ({pl.percent}%)</Tag>;
                } else if (pl.isBalanced && pl.readyPcs > 0) {
                  return <Tag color="blue" style={{ fontWeight: 700 }}>0 pcs (0%)</Tag>;
                } else if (pl.isPending) {
                  return <Tag color="default" style={{ fontWeight: 600 }}>Date Not Exceeded</Tag>;
                }
                return '-';
              }
            }
          ]}
        />
      </Modal>

      {/* 4. Track Issue - Timber Modal (View CFT Issue) */}
      <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "95%" }}>
            <div>
              <span style={{ fontSize: 18, fontWeight: 700 }}>Track Workflow - Timber</span>
              <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
                {selectedWO?.product_name} (WO #{selectedWO?.wo_number || selectedWO?.wo_id}, PI #{selectedWO?.pi_order_no})
              </div>
            </div>
            <Space>
              <Button
                type="primary"
                icon={<Icons.FileExcelOutlined />}
                onClick={handleExportTimberExcel}
                style={{ borderRadius: 6, fontWeight: 600, backgroundColor: "#107c41", borderColor: "#107c41" }}
              >
                Export Excel (.xlsx)
              </Button>
            </Space>
          </div>
        }
        open={trackIssueModalVisible}
        onCancel={() => setTrackIssueModalVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: customTextMain }}>
            Timber (Wood) Requirements & Issued Quantities
          </div>
          <Table
            dataSource={timberTableData.rows}
            rowKey="key"
            pagination={false}
            size="small"
            bordered
            columns={[
              {
                title: "PO No.",
                key: "po_no",
                width: 120,
                render: (_: any, __: any, index: number) => `Exp. ${selectedWO?.pi_order_no || piOrderNo}/${String(index + 1).padStart(2, '0')}`,
              },
              {
                title: "Size/Wood Type",
                dataIndex: "size_wood_type",
                key: "size_wood_type",
                width: 170,
              },
              {
                title: "Mix",
                dataIndex: "mix",
                key: "mix",
                width: 80,
                render: (val: any) => val || "-",
              },
              {
                title: "Width",
                children: timberTableData.activeWidthKeys.map((w) => ({
                  title: `${w}"`,
                  key: `width_${w}`,
                  width: 75,
                  align: "center" as const,
                  render: (_: any, record: any) => {
                    const reqQty = record.widths?.[w];
                    const issuedQty = record.issued_widths?.[w] || 0;

                    if (issuedQty > 0 && reqQty !== undefined && reqQty !== null && reqQty !== 0) {
                      const color = getIssuedColor(issuedQty, reqQty);
                      return (
                        <span>
                          <span>{reqQty}</span>
                          <span style={{ color: "#8c8c8c" }}>/</span>
                          <span style={{ color, fontWeight: 700 }}>{issuedQty}</span>
                        </span>
                      );
                    } else if (issuedQty > 0) {
                      const color = getIssuedColor(issuedQty, 0);
                      return (
                        <span style={{ color, fontWeight: 700 }}>{issuedQty}</span>
                      );
                    } else if (reqQty !== undefined && reqQty !== null && reqQty !== 0) {
                      return <span>{reqQty}</span>;
                    }
                    return <span style={{ color: "#bfbfbf" }}>-</span>;
                  },
                })),
              },
              {
                title: "Req. CFT",
                dataIndex: "cft",
                key: "cft",
                width: 95,
                render: (cft: number) => <strong>{cft}</strong>,
              },
              {
                title: "Issued",
                dataIndex: "issued_cft",
                key: "issued_cft",
                width: 110,
                render: (issuedCft: number, record: any) => {
                  const reqCft = record.cft || 0;
                  const color = getIssuedColor(issuedCft, reqCft);
                  return (
                    <span style={{ fontWeight: 700, color }}>
                      {issuedCft > 0 ? `${issuedCft} cft` : "0 cft"}
                    </span>
                  );
                }
              },
              {
                title: "Pcs",
                dataIndex: "pcs",
                key: "pcs",
                width: 80,
                render: (pcs: number) => pcs || "-",
              },
              {
                title: "Price",
                dataIndex: "price",
                key: "price",
                width: 90,
                render: (price: number) => `₹ ${price}`,
              },
              {
                title: "Total Amt",
                dataIndex: "total_amount",
                key: "total_amount",
                width: 120,
                render: (amt: number) => (
                  <strong style={{ color: customTextMain }}>₹ {amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                ),
              }
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: darkMode ? '#1e293b' : '#fafafa', fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={3 + timberTableData.activeWidthKeys.length} align="right">
                  Grand Total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  {timberTableData.totalReqCft.toFixed(4)} cft
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <span style={{ color: '#10b981' }}>{timberTableData.totalIssuedCft.toFixed(4)} cft</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5}>
                  ₹ {timberTableData.totalAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          {matchingIssueMasterRecords.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: customTextMain }}>
                Issued Timber Vouchers
              </div>
              <Table
                dataSource={matchingIssueMasterRecords}
                rowKey="im_id"
                pagination={false}
                size="small"
                bordered
                columns={[
                  {
                    title: 'Issue #',
                    dataIndex: 'im_id',
                    key: 'im_id',
                    render: (id) => `ISS-${id}`
                  },
                  {
                    title: 'Date',
                    dataIndex: 'im_created_on',
                    key: 'im_created_on',
                    render: (val) => val ? dayjs(val).format('DD-MMM-YYYY') : '-'
                  },
                  {
                    title: 'Issued To',
                    dataIndex: 'employee_name',
                    key: 'employee_name'
                  },
                  {
                    title: 'Total Amount',
                    dataIndex: 'im_total',
                    key: 'im_total',
                    render: (amt) => amt ? `₹ ${Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'
                  }
                ]}
              />
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
};

export default DailyProgressProductDetail;
